import React, { useEffect, useState } from 'react';
import { TableContainer, Paper, Table, TableBody, TableRow, TableCell, styled, Typography, Box, Divider, List, ListItemButton, ListItemText, Stack, TableHead, Chip, IconButton, Alert } from "@mui/material";
import { DateTime } from "luxon";
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { queryEmployeeFromStateOrDb } from '../../utils/queryEmployeeFromStateOrDb';
import { Employee } from '../../types/Employee';
import { TabPanel } from '../TabPanel';
import { queryVacationByIdNumber } from '../../utils/queryVacations';
import { Vacation } from '../../types/Vacation';
import { NavigateNextRounded } from '@mui/icons-material';
import { navigate } from '../../store/slices/breadcrumbSlice';
import { ChipIcon, ChipColor } from '../ChipUtils';
import { EmployeeVacationRequests } from './EmployeeVacationRequests';
import { EmployeeReceiptRequests } from './EmployeeReceiptRequests';
import { EmployeePassportRequests } from './EmployeePassportRequests';
import { EmployeeUniformRequests } from './EmployeeUniformRequests';
import { useAppSelector } from '../../store';
import { ROLES_GROUP } from '../../security/Role';
import { SecureComponent } from '../../security/SecureComponent';
import { SecureModule } from '../../security/SecureModule';
import { EmployeePageContext } from './EmployeePage';

interface RequestSideBoxProps {
  selected: number,
  onChange: (i: number) => void,
};
const RequestSideBox = (props: RequestSideBoxProps) => {
  const { selected, onChange } = props;

  return (
    <Box component={Paper} sx={{ width: '100%', maxWidth: 250, bgcolor: 'background.paper' }}>
      <SecureComponent roles={ROLES_GROUP.SERVICES}>
        <>
          <Typography sx={{ mx: 2, }} variant="overline" component="h6">SERVICES</Typography>
          <Divider />
          <nav aria-label="services">
            <List>
              <SecureComponent roles={ROLES_GROUP.VACATION}>
                <ListItemButton selected={selected === 0} onClick={() => onChange(0)}>
                  <ListItemText primary="Vacation" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.IQAMA}>
                <ListItemButton selected={selected === 1} onClick={() => onChange(1)}>
                  <ListItemText primary="Iqama Renewal" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.TASHERA}>
                <ListItemButton selected={selected === 2} onClick={() => onChange(2)}>
                  <ListItemText primary="Tashera Payments" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.OTHER_FILES}>
                <ListItemButton selected={selected === 3} onClick={() => onChange(3)}>
                  <ListItemText primary="Other Files" />
                </ListItemButton>
              </SecureComponent>
            </List>
          </nav>
        </>
      </SecureComponent>
      <SecureComponent roles={ROLES_GROUP.DASHBOARD}>
        <>
          <Typography sx={{ mx: 2, }} variant="overline" component="h6">DASHBOARD</Typography>
          <Divider />
          <nav aria-label="dashboard">
            <List>
              <SecureComponent roles={ROLES_GROUP.MEDICAL}>
                <ListItemButton selected={selected === 4} onClick={() => onChange(4)}>
                  <ListItemText primary="Medical" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.UMRAH}>
                <ListItemButton selected={selected === 5} onClick={() => onChange(5)}>
                  <ListItemText primary="Umrah" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.PASSPORT}>
                <ListItemButton selected={selected === 6} onClick={() => onChange(6)}>
                  <ListItemText primary="Passport" />
                </ListItemButton>
              </SecureComponent>
              <SecureComponent roles={ROLES_GROUP.UNIFORM}>
                <ListItemButton selected={selected === 7} onClick={() => onChange(7)}>
                  <ListItemText primary="Uniform" />
                </ListItemButton>
              </SecureComponent>
            </List>
          </nav>
        </>
      </SecureComponent>
    </Box>
  );
};

const getTitleByIndex = (i: number) => {
  if (i === 0) return "Vacation Requests";
  else if (i === 1) return "Iqama Renewal Requests";
  else if (i === 2) return "Tashera Payments";
  else if (i === 3) return "Other Files";
  else if (i === 4) return "Medical Requests";
  else if (i === 5) return "Umrah Requests";
  else if (i === 6) return "Passport Requests";
  else if (i === 7) return "Uniform Requests";
  else return "404! Index not found!";
};

export const RequestsBox = () => {
  const { employee }: EmployeePageContext = useOutletContext();
  const [selected, setSelected] = useState(0);

  const requestsOnChange = (value: number) => {
    setSelected(value);
  };

  if (!employee) {
    return (
      <Box>
        <Alert severity='info'>
          Getting employee information...
        </Alert>
      </Box>
    );
  }

  return (
    <SecureModule roles={[...ROLES_GROUP.SERVICES, ...ROLES_GROUP.DASHBOARD]}>
      <Stack direction="row" spacing={2}>
        <SecureComponent roles={[...ROLES_GROUP.SERVICES, ...ROLES_GROUP.DASHBOARD]}>
          <Box flexGrow={1}>
            <Typography variant='h5'>{getTitleByIndex(selected)}</Typography>
            <TabPanel index={0} value={selected}>
              <SecureModule roles={ROLES_GROUP.VACATION}>
                <EmployeeVacationRequests employee={employee} />
              </SecureModule>
            </TabPanel>
            <TabPanel index={1} value={selected}>
              <SecureModule roles={ROLES_GROUP.IQAMA}>
                <EmployeeReceiptRequests employee={employee} type='Iqama' />
              </SecureModule>
            </TabPanel>
            <TabPanel index={2} value={selected}>
              <SecureModule roles={ROLES_GROUP.TASHERA}>
                <EmployeeReceiptRequests employee={employee} type='Vacation' />
              </SecureModule>
            </TabPanel>
            <TabPanel index={3} value={selected}>
              <SecureModule roles={ROLES_GROUP.OTHER_FILES}>
                <EmployeeReceiptRequests employee={employee} type='Others' />
              </SecureModule>
            </TabPanel>
            
            <TabPanel index={4} value={selected}>
              <SecureComponent roles={ROLES_GROUP.MEDICAL}>
                <Paper sx={{ p: 1 }}><Typography variant='caption'>Not yet implemented...</Typography></Paper>
              </SecureComponent>
            </TabPanel>
            <TabPanel index={5} value={selected}>
             <SecureComponent roles={ROLES_GROUP.UMRAH}>
                <Paper sx={{ p: 1 }}><Typography variant='caption'>Not yet implemented...</Typography></Paper>
              </SecureComponent>
            </TabPanel>
            <TabPanel index={6} value={selected}>
              <SecureModule roles={ROLES_GROUP.PASSPORT}>
                <EmployeePassportRequests employee={employee} />
              </SecureModule>
            </TabPanel>
            <TabPanel index={7} value={selected}>
              <SecureModule roles={ROLES_GROUP.UNIFORM}>
                <EmployeeUniformRequests employee={employee} />
              </SecureModule>
            </TabPanel>
          </Box>
        </SecureComponent>
        <SecureModule roles={[...ROLES_GROUP.SERVICES, ...ROLES_GROUP.DASHBOARD]}>
          <RequestSideBox selected={selected} onChange={requestsOnChange} />
        </SecureModule>
      </Stack>
    </SecureModule>
  );
};
